import React from 'react';
import { useFormContext } from 'react-hook-form';
import { LenderMappingsFormValues } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/types';
import { CurrencyInputField, InputField, NumberFormatField } from 'components/form';
import { LenderFieldMap } from '@trnsact/trnsact-shared-types';
import { sanitizeFormKey } from 'modules/vendorOpportunity/dynamicLenderSubmissionMappings/lib';

interface Props {
  field: LenderFieldMap;
  inputProps: any;
  formatter?: (value: string) => string;
  onValueChange: (lenderProperty: string, newValue: any) => void;
}

export const TextMappingField = ({ field, inputProps, formatter, onValueChange }: Props) => {
  const { control } = useFormContext<LenderMappingsFormValues>();

  const type = field.details?.fieldInputType;
  const isRequired = field.details?.required === 'REQUIRED';
  const fieldName = `mappingsByLenderProperty.${sanitizeFormKey(field.lenderProperty!)}.mappedValue` as const;
  const rules = {
    validate: (value: unknown) => {
      if (isRequired && (value === null || value === undefined || value === '')) return 'This field is required';
      const num = Number(value);
      if (inputProps.min !== undefined && num < Number(inputProps.min)) return `Min value is ${inputProps.min}`;
      if (inputProps.max !== undefined && num > Number(inputProps.max)) return `Max value is ${inputProps.max}`;
      return true;
    },
  };

  const hasError = !!field.details?.errors?.length;
  const errorMessages = field.details?.errors?.map(e => e.message).join(', ');
  const label = field.details?.displayName ?? '';

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (field.lenderProperty) onValueChange(field.lenderProperty, e.target.value);
  };

  const range: [number | undefined, number | undefined] | undefined =
    inputProps.min !== undefined || inputProps.max !== undefined
      ? [
          inputProps.min !== undefined ? Number(inputProps.min) : undefined,
          inputProps.max !== undefined ? Number(inputProps.max) : undefined,
        ]
      : undefined;

  if (type === 'FLOAT' || type === 'INTEGER') {
    return (
      <NumberFormatField
        label={label}
        control={control}
        name={fieldName}
        rules={rules}
        range={range}
        textFieldProps={{
          error: hasError,
          required: isRequired,
          variant: 'outlined',
          size: 'small',
          helperText: errorMessages,
          onBlur: handleBlur,
        }}
      />
    );
  }

  if (type === 'MONEY') {
    return (
      <CurrencyInputField
        label={label}
        control={control}
        name={fieldName}
        rules={rules}
        range={range}
        textFieldProps={{
          error: hasError,
          required: isRequired,
          variant: 'outlined',
          helperText: errorMessages,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            if (field.lenderProperty) {
              onValueChange(field.lenderProperty, e.target.value.replace(/[^0-9.-]+/g, ''));
            }
          },
        }}
      />
    );
  }

  return (
    <InputField
      label={label}
      control={control}
      name={fieldName}
      rules={rules}
      formatter={formatter}
      inputProps={{
        error: hasError,
        required: isRequired,
        variant: 'outlined',
        helperText: errorMessages,
        onBlur: handleBlur,
      }}
    />
  );
};
